import { Injectable } from '@angular/core';
import type { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { createUrlTreeFromSnapshot, Router } from '@angular/router';

import type { Observable } from 'rxjs';
import { combineLatest, of } from 'rxjs';
import { first, map } from 'rxjs/operators';

import { getProductCodenameFromSubscription } from '@recruitee/billing-plans';
import { BillingPlanTier } from '@recruitee/products-types';

import { isPp24 } from '../../../helpers/is-pp24';
import { readyOnce } from '../../../helpers/rx-operators';
import {
  PricingFeatureFacade,
  PricingFeatureObservableModel,
} from '../../../types/pricing-feature.types';
import { ProductOrderStepUrl } from '../product-order.types';

@Injectable({
  providedIn: 'root',
})
export class ProductOrderDowngradeGuard {
  constructor(
    private router: Router,
    private facade: PricingFeatureFacade,
    private model$: PricingFeatureObservableModel,
  ) {}

  public canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> {
    const nav = this.router.getCurrentNavigation();

    if (
      (nav && nav.extras.state && nav.extras.state.downgradeScreenViewed === true) ||
      route.routeConfig!.path === ProductOrderStepUrl.Downgrade ||
      route.routeConfig!.path === ProductOrderStepUrl.Migration
    ) {
      return of(true);
    }

    return combineLatest([
      this.getProduct(route.parent?.params.productId),
      this.model$.pipe(readyOnce),
    ]).pipe(
      first(),
      map(([product, { subscription, isPP24 }]) => {
        const subscriptionProductCodename = getProductCodenameFromSubscription(subscription);

        if (product.custom) return true;

        const shouldVisitDowngrade = this.shouldGoThroughDowngradeStep(
          product.tier,
          subscriptionProductCodename,
          isPP24,
        );

        if (shouldVisitDowngrade) {
          return createUrlTreeFromSnapshot(route, ['../', ProductOrderStepUrl.Downgrade]);
        }

        return true;
      }),
    );
  }

  private getProduct(id: string) {
    return this.facade.getBillingPlanById(parseInt(id, 10));
  }

  private shouldGoThroughDowngradeStep(
    selectedProductCodename: BillingPlanTier,
    subscriptionProductCodename: BillingPlanTier | undefined,
    isPP24: boolean,
  ): boolean {
    if (!isPP24 && this.migratingToPP2024(selectedProductCodename, subscriptionProductCodename))
      return false;
    if (selectedProductCodename === subscriptionProductCodename) return false;

    return [BillingPlanTier.Launch, BillingPlanTier.Start].includes(
      <BillingPlanTier>selectedProductCodename,
    );
  }

  private migratingToPP2024(
    selectedProductCodename: BillingPlanTier,
    subscriptionProductCodename: BillingPlanTier | undefined,
  ) {
    return (
      isPp24(selectedProductCodename) &&
      (!subscriptionProductCodename || !isPp24(subscriptionProductCodename))
    );
  }
}
